import { createFileRoute, Link } from '@tanstack/react-router'
import { ArrowLeft, DollarSign, FileText, MapPin, Target, Users } from 'lucide-react'
import { useState } from 'react'
import HeaderShared from '@/components/_shared/header-shared'
import InvestmentModal from '@/components/investment-modal'
import ProjectMap from '@/components/project-map'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
	formatDescription,
	formatDuration,
	getBiodiversityCreditsData,
	getProjectTokensData,
	type Project,
} from '@/features/projects/helpers/project-helpers'
import { fetchProjectById } from '@/features/projects/queries/project-queries'

// Project tab enum for type safety
const ProjectTab = {
	OVERVIEW: 'overview',
	FINANCIAL: 'financial',
	DOCUMENTS: 'documents',
	REVIEWS: 'reviews',
	SITES: 'sites',
} as const

type ProjectTabType = (typeof ProjectTab)[keyof typeof ProjectTab]

// Tab configuration for better maintainability
const TAB_CONFIG = [
	{
		key: ProjectTab.OVERVIEW,
		label: 'Overview',
	},
	{
		key: ProjectTab.FINANCIAL,
		label: 'Financial',
	},
	{
		key: ProjectTab.DOCUMENTS,
		label: 'Documents',
	},
	{
		key: ProjectTab.REVIEWS,
		label: 'Reviews',
	},
	{
		key: ProjectTab.SITES,
		label: 'Sites',
	},
] as const

export const Route = createFileRoute('/_authenticated/_sidebar/project/$id')({
	component: ProjectDetail,
	loader: async ({ params }) => {
		const { id } = params
		try {
			/* TODO: Tanstack query for the fetch into loader */
			const response = await fetchProjectById(id)
			return response.data
		} catch (error) {
			throw error
		}
	},
})

function ProjectDetail() {
	const project = Route.useLoaderData() as Project
	const [activeTab, setActiveTab] = useState<ProjectTabType>(ProjectTab.OVERVIEW)
	const [isInvestmentModalOpen, setIsInvestmentModalOpen] = useState(false)

	// Pre-calculate data using helper functions
	const projectTokensData = getProjectTokensData(project)
	const biodiversityCreditsData = getBiodiversityCreditsData(project)
	const projectName = project.name || 'Untitled Project'
	const projectDescription = formatDescription(project.description)
	const projectGrower = project.grower || 'Unknown'
	const projectActuator = project.actuator || 'Unknown'
	const projectObserver = project.observer || 'Not specified'
	const projectDuration = formatDuration(project.duration)
	const projectStartDate = project.start_date || 'Not specified'
	const projectStatus = project.status || 'Unknown'
	const projectSmartContract = project.project_smart_contract_address || 'Not deployed'
	const projectCoverImage = project.cover_image || '/placeholder-project.jpg'
	const projectTags = project.tags || []
	const projectSdgs = project.sustainable_development_goals || []
	const projectSites = project.sites_data || []
	const projectReviews = project.reviews_data || []
	const projectAttachments = project.attachments_data || []

	// Render tab content based on active tab
	const renderTabContent = () => {
		switch (activeTab) {
			case ProjectTab.OVERVIEW:
				return (
					<div className="space-y-8">
						<div className="grid grid-cols-1 xl:grid-cols-3 gap-6 xl:gap-8">
							<div className="xl:col-span-2 space-y-6 xl:space-y-8">
								<Card>
									<CardHeader>
										<CardTitle>Project Description</CardTitle>
									</CardHeader>
									<CardContent>
										<p className="text-muted-foreground leading-relaxed whitespace-pre-line">
											{projectDescription}
										</p>
									</CardContent>
								</Card>

								<Card>
									<CardHeader>
										<CardTitle>Project Tags</CardTitle>
									</CardHeader>
									<CardContent>
										<div className="flex flex-wrap gap-2">
											{projectTags.length > 0 ? (
												projectTags.map((tag) => (
													<Badge key={tag} variant="secondary">
														{tag}
													</Badge>
												))
											) : (
												<p className="text-muted-foreground">No tags available</p>
											)}
										</div>
									</CardContent>
								</Card>

								<Card>
									<CardHeader>
										<CardTitle>Project Information</CardTitle>
									</CardHeader>
									<CardContent>
										<div className="space-y-4">
											<div className="flex justify-between">
												<span className="text-muted-foreground">Status</span>
												<Badge variant="outline">{projectStatus}</Badge>
											</div>
											<div className="flex justify-between">
												<span className="text-muted-foreground">Grower</span>
												<span className="font-medium">{projectGrower}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-muted-foreground">Actuator</span>
												<span className="font-medium">{projectActuator}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-muted-foreground">Observer</span>
												<span className="font-medium">{projectObserver}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-muted-foreground">Duration</span>
												<span className="font-medium">{projectDuration}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-muted-foreground">Start Date</span>
												<span className="font-medium">{projectStartDate}</span>
											</div>
											<div className="flex justify-between">
												<span className="text-muted-foreground">Smart Contract</span>
												<span className="font-mono text-sm">{projectSmartContract}</span>
											</div>
										</div>
									</CardContent>
								</Card>
							</div>

							<div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-1 gap-6 xl:space-y-0">
								<Card>
									<CardHeader>
										<CardTitle>Project Map</CardTitle>
									</CardHeader>
									<CardContent>
										{projectSites.length > 0 ? (
											<ProjectMap sites={projectSites as any} projectTitle={projectName} />
										) : (
											<div className="text-center py-8 text-muted-foreground">
												<MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
												<p>No site location data available for this project.</p>
											</div>
										)}
									</CardContent>
								</Card>

								<Card>
									<CardHeader>
										<CardTitle>SDG Impacts</CardTitle>
									</CardHeader>
									<CardContent>
										<div className="space-y-2">
											{projectSdgs.length > 0 ? (
												projectSdgs.map((sdg) => (
													<div key={sdg.goal_number} className="flex items-center gap-3">
														{sdg.icon_external_url && (
															<div className="flex-shrink-0">
																<img
																	src={sdg.icon_external_url}
																	alt={`SDG ${sdg.goal_number}`}
																	className="w-12 h-12 object-contain"
																/>
															</div>
														)}
														<div className="flex-1">
															{/* <Badge variant="secondary">SDG {sdg.goal_number}</Badge> */}
															<span className="text-sm font-bold ml-2">{sdg.name}</span>
														</div>
													</div>
												))
											) : (
												<p className="text-muted-foreground">No SDGs available</p>
											)}
										</div>
									</CardContent>
								</Card>
							</div>
						</div>
					</div>
				)

			case ProjectTab.FINANCIAL:
				return (
					<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<DollarSign className="h-5 w-5" />
									Project Tokens & Credits
								</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="space-y-4">
									<div className="flex justify-between">
										<span className="text-muted-foreground">Project Tokens Released</span>
										<span className="font-semibold">{projectTokensData.released}</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Project Tokens Expected</span>
										<span className="font-semibold">{projectTokensData.expected}</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Biodiversity Credits Released</span>
										<span className="font-semibold">{biodiversityCreditsData.released}</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Biodiversity Credits Expected</span>
										<span className="font-semibold">{biodiversityCreditsData.expected}</span>
									</div>
								</div>
							</CardContent>
						</Card>

						<Card>
							<CardHeader>
								<CardTitle>Project Statistics</CardTitle>
							</CardHeader>
							<CardContent>
								<div className="space-y-4">
									<div className="flex justify-between">
										<span className="text-muted-foreground">Sites</span>
										<span className="font-medium">{projectSites.length}</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Reviews</span>
										<span className="font-medium">{projectReviews.length}</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Status</span>
										<span className="font-medium">{projectStatus}</span>
									</div>
									<div className="flex justify-between">
										<span className="text-muted-foreground">Smart Contract</span>
										<span className="font-medium text-xs">{projectSmartContract}</span>
									</div>
								</div>
							</CardContent>
						</Card>
					</div>
				)

			case ProjectTab.DOCUMENTS:
				return (
					<div className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<FileText className="h-5 w-5" />
									Project Attachments ({projectAttachments.length})
								</CardTitle>
							</CardHeader>
							<CardContent>
								{projectAttachments.length > 0 ? (
									<div className="space-y-4">
										{projectAttachments.map((attachment) => (
											<div key={attachment.id} className="border rounded-lg p-4 space-y-3">
												<div className="flex items-center justify-between">
													<h4 className="font-medium">{attachment.name}</h4>
													<Badge variant="outline">{attachment.status}</Badge>
												</div>
												{attachment.description && (
													<p className="text-sm text-muted-foreground">{attachment.description}</p>
												)}
												<div className="flex items-center gap-2">
													<Button
														variant="outline"
														size="sm"
														onClick={() => window.open(attachment.file_internal, '_blank')}
													>
														<FileText className="h-4 w-4 mr-2" />
														View File
													</Button>
													<Button
														variant="outline"
														size="sm"
														onClick={() => {
															const link = document.createElement('a')
															link.href = attachment.file_internal
															link.download = attachment.name
															link.click()
														}}
													>
														<FileText className="h-4 w-4 mr-2" />
														Download
													</Button>
												</div>
												<div className="text-xs text-muted-foreground">
													Created: {new Date(attachment.created_at).toLocaleDateString()}
												</div>
											</div>
										))}
									</div>
								) : (
									<div className="text-center py-8 text-muted-foreground">
										<FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
										<p>No attachments available for this project yet.</p>
									</div>
								)}
							</CardContent>
						</Card>
					</div>
				)

			case ProjectTab.REVIEWS:
				return (
					<div className="space-y-6">
						<Card>
							<CardHeader>
								<CardTitle className="flex items-center gap-2">
									<Users className="h-5 w-5" />
									Project Reviews ({projectReviews.length})
								</CardTitle>
							</CardHeader>
							<CardContent>
								{projectReviews.length > 0 ? (
									<div className="space-y-4">
										{projectReviews.map((review) => (
											<div key={review.id} className="border rounded-lg p-4 space-y-3">
												<div className="flex items-center justify-between">
													<h4 className="font-medium">Review #{review.id}</h4>
													<div className="flex items-center gap-2">
														<Badge variant="outline">{review.status}</Badge>
														<Badge variant="secondary">Year: {review.reference_year}</Badge>
													</div>
												</div>
												<div className="grid grid-cols-2 gap-4 text-sm">
													<div>
														<span className="text-muted-foreground">Project:</span>
														<span className="ml-2">#{review.project}</span>
													</div>
													<div>
														<span className="text-muted-foreground">Site:</span>
														<span className="ml-2">#{review.site}</span>
													</div>
												</div>
												{review.attachments && review.attachments.length > 0 && (
													<div className="flex items-center gap-2 text-sm">
														<span className="text-muted-foreground">Attachments:</span>
														<Badge variant="secondary">{review.attachments.length}</Badge>
													</div>
												)}
												<div className="text-xs text-muted-foreground">
													Created: {new Date(review.created_at).toLocaleDateString()}
												</div>
											</div>
										))}
									</div>
								) : (
									<div className="text-center py-8 text-muted-foreground">
										<Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
										<p>No reviews available for this project yet.</p>
									</div>
								)}
							</CardContent>
						</Card>
					</div>
				)

			case ProjectTab.SITES:
				return (
					<div className="space-y-6">
						{projectSites.length > 0 ? (
							projectSites.map((site) => (
								<Card key={site.id}>
									<CardHeader>
										<CardTitle className="flex items-center gap-2">
											<MapPin className="h-5 w-5" />
											{site.location_name}
											<Badge variant="outline">Site #{site.id}</Badge>
										</CardTitle>
									</CardHeader>
									<CardContent>
										<div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
											{/* Site Information */}
											<div className="space-y-4">
												{site.description && (
													<div>
														<h4 className="font-medium mb-2">Description</h4>
														<p className="text-sm text-muted-foreground">{site.description}</p>
													</div>
												)}
												<div className="space-y-2">
													<div className="flex items-center gap-2 text-sm">
														<MapPin className="h-4 w-4" />
														<span className="text-muted-foreground">Coordinates:</span>
														<span>
															{site.location?.coordinates ? (
																<>
																	{site.location.coordinates[1]?.toFixed(4)},{' '}
																	{site.location.coordinates[0]?.toFixed(4)}
																</>
															) : (
																'No coordinates available'
															)}
														</span>
													</div>
													<div className="flex items-center gap-2 text-sm">
														<span className="text-muted-foreground">Region:</span>
														<span>{site.location_aal1}</span>
													</div>
													<div className="flex items-center gap-2 text-sm">
														<span className="text-muted-foreground">Country:</span>
														<span>{site.location_country}</span>
													</div>
													<div className="flex items-center gap-2 text-sm">
														<span className="text-muted-foreground">Reviews:</span>
														<Badge variant="secondary">{site.reviews?.length || 0}</Badge>
													</div>
													<div className="flex items-center gap-2 text-sm">
														<span className="text-muted-foreground">Created:</span>
														<span>{new Date(site.created_at).toLocaleDateString()}</span>
													</div>
												</div>
											</div>

											{/* Site Map */}
											<div>
												<h4 className="font-medium mb-2">Location Map</h4>
												<ProjectMap
													sites={[site] as any}
													projectTitle={`${projectName} - Site ${site.id}`}
												/>
											</div>
										</div>
									</CardContent>
								</Card>
							))
						) : (
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center gap-2">
										<MapPin className="h-5 w-5" />
										Project Sites
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="text-center py-8 text-muted-foreground">
										<MapPin className="h-12 w-12 mx-auto mb-4 opacity-50" />
										<p>No sites available for this project yet.</p>
									</div>
								</CardContent>
							</Card>
						)}
					</div>
				)

			default:
				return null
		}
	}

	return (
		<div className="min-h-screen bg-background">
			<HeaderShared />

			{/* Header */}
			<div className="border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
				<div className="container mx-auto px-4 py-6">
					<div className="flex items-center gap-4 mb-6">
						<Link
							to="/projects"
							className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
						>
							<ArrowLeft className="h-4 w-4" />
							Back to Projects
						</Link>
					</div>

					<div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
						<div className="lg:col-span-2">
							<div className="flex items-start gap-4 mb-4">
								<div className="flex-1">
									<h1 className="text-3xl font-bold mb-2">{projectName}</h1>
									<p className="text-muted-foreground text-lg mb-4">{projectDescription}</p>
									<div className="flex flex-wrap gap-2">
										<Badge variant="secondary">{projectStatus}</Badge>
										{projectTags.slice(0, 2).map((tag) => (
											<Badge key={tag} variant="outline">
												{tag}
											</Badge>
										))}
									</div>
								</div>
							</div>
						</div>

						<div className="space-y-4">
							<Card>
								<CardHeader>
									<CardTitle className="flex items-center gap-2">
										<Target className="h-5 w-5" />
										Project Stats
									</CardTitle>
								</CardHeader>
								<CardContent>
									<div className="space-y-4">
										<div className="flex justify-between">
											<span className="text-muted-foreground">Sites</span>
											<span className="font-semibold">{projectSites.length}</span>
										</div>
										<div className="flex justify-between">
											<span className="text-muted-foreground">Reviews</span>
											<span className="font-semibold">{projectReviews.length}</span>
										</div>
										<div className="flex justify-between">
											<span className="text-muted-foreground">Attachments</span>
											<span className="font-semibold">{projectAttachments.length}</span>
										</div>
										<div className="flex justify-between">
											<span className="text-muted-foreground">SDGs</span>
											<span className="font-semibold">{projectSdgs.length}</span>
										</div>
									</div>
								</CardContent>
							</Card>

							<Button
								onClick={() => setIsInvestmentModalOpen(true)}
								className="w-full h-12 text-lg font-semibold cursor-pointer"
							>
								Invest in Project
							</Button>
						</div>
					</div>
				</div>
			</div>

			{/* Main Content */}
			<div className="container mx-auto px-4 py-8">
				<div className="space-y-8">
					{/* Project Image */}
					<div className="relative h-64 md:h-96 rounded-lg overflow-hidden">
						<img src={projectCoverImage} alt={projectName} className="w-full h-full object-cover" />
						<div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
					</div>

					{/* Navigation Buttons */}
					<div className="space-y-6 sm:space-y-8">
						<div className="flex flex-wrap gap-2 sm:gap-3 overflow-x-auto pb-2">
							{TAB_CONFIG.map((tab) => (
								<Button
									key={tab.key}
									variant={activeTab === tab.key ? 'default' : 'outline'}
									size="sm"
									onClick={() => setActiveTab(tab.key)}
									className="text-xs sm:text-sm whitespace-nowrap flex-shrink-0"
								>
									{tab.label}
								</Button>
							))}
						</div>

						{renderTabContent()}
					</div>
				</div>
			</div>

			{/* Investment Modal */}
			<InvestmentModal
				isOpen={isInvestmentModalOpen}
				onClose={() => setIsInvestmentModalOpen(false)}
				projectTitle={projectName}
			/>
		</div>
	)
}
